import { ButtonSpinner } from "@/components/ui/Button";

interface PlanCardProps {
  title: string;
  price: string;
  subtext?: string;
  description?: string;
  /** The main thing the plan buys, e.g. "25,000 credits". */
  credits?: string;
  /** "a month" or "once". */
  creditsNote?: string;
  /** What the credits mean in practice. */
  subline?: string;
  /** Label and value rows, the same rows in the same order on every card. */
  specs: [string, string][];
  actionLabel: string;
  /** "upgrade" is the main call to action, "downgrade" a quiet one, "status" a non-clickable label. */
  actionKind?: "upgrade" | "downgrade" | "status";
  current?: boolean;
  highlighted?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
  onSelect?: () => void;
}

/**
 * One plan in the plan grid, shaped like the cards on spidra.io/pricing. The highlighted plan is drawn in deep blue.
 */
const PlanCard = ({
  title,
  price,
  subtext = "month",
  description = "",
  credits,
  creditsNote,
  subline,
  specs,
  actionLabel,
  actionKind = "upgrade",
  current = false,
  highlighted = false,
  isLoading = false,
  disabled = false,
  onSelect,
}: PlanCardProps) => {
  const isFree = title.toLowerCase() === "free";
  const blue = highlighted;
  const muted = blue ? "text-white/70" : "text-fg-muted";

  // One button style per job: the main pick is solid, a downgrade is outlined, the current plan is a quiet label.
  const buttonClass = (() => {
    if (actionKind === "status")
      return blue
        ? "bg-white/15 text-white cursor-default"
        : "bg-surface-muted text-fg-muted border border-line cursor-default";
    if (actionKind === "downgrade")
      return "bg-surface text-fg border border-line-strong hover:bg-surface-muted cursor-pointer";
    return blue
      ? "bg-white text-primary hover:bg-white/90 shadow-card cursor-pointer"
      : "bg-primary-subtle text-primary-text hover:bg-primary hover:text-white cursor-pointer";
  })();

  return (
    <div
      className={`relative flex h-full flex-col overflow-hidden p-6 xl:p-7 ${blue ? "bg-deep text-white" : "bg-surface text-fg"}`}
    >
      {blue && <span aria-hidden="true" className="dot-field" />}

      <div className="relative flex items-center justify-between gap-2">
        <h3 className="text-base font-medium">{title}</h3>
        {(current || blue) && (
          <span
            className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${blue ? "bg-white/15 text-white" : "bg-primary-subtle text-primary-text"}`}
          >
            {current ? "Your plan" : "Most popular"}
          </span>
        )}
      </div>
      {description && (
        <p className={`relative mt-1.5 min-h-10 text-13 leading-snug xl:min-h-[3lh] ${muted}`}>{description}</p>
      )}

      <p className="relative mt-5 flex items-baseline gap-1.5">
        {/* ui-lint-ignore: display price, matches the plan cards on spidra.io/pricing */}
        <span className="text-[40px] font-medium leading-none tracking-[-0.03em] tabular-nums">
          {price}
        </span>
        <span className={`text-sm ${blue ? "text-white/70" : "text-fg-muted"}`}>
          {!isFree && "/ "}
          {subtext}
        </span>
      </p>

      {credits && (
        <div
          className={`relative mt-5 rounded-card px-4 py-3 ${blue ? "bg-white/10" : "bg-primary-subtle"}`}
        >
          <p className="text-15 font-medium tabular-nums">
            {credits} {creditsNote && <span className={`font-normal ${muted}`}>{creditsNote}</span>}
          </p>
          {subline && <p className={`mt-0.5 text-xs leading-snug ${muted}`}>{subline}</p>}
        </div>
      )}

      <button
        type="button"
        className={`relative mt-5 flex h-10 w-full items-center justify-center gap-2 rounded-control text-sm font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 ${blue ? "focus-visible:outline-white" : "focus-visible:outline-primary"} ${actionKind === "status" ? "" : "disabled:opacity-60"} ${buttonClass}`}
        onClick={actionKind === "status" ? undefined : onSelect}
        disabled={actionKind === "status" || disabled || isLoading}
        aria-busy={isLoading || undefined}
      >
        {isLoading && <ButtonSpinner />}
        {actionLabel}
      </button>

      <dl className="relative mt-6">
        {specs.map(([label, value]) => (
          <div
            key={label}
            className={`flex items-center justify-between gap-3 border-t py-2.5 text-13 ${blue ? "border-white/15" : "border-line"}`}
          >
            <dt className={muted}>{label}</dt>
            <dd
              className={`text-right font-medium tabular-nums ${value === "No" ? (blue ? "text-white/50" : "text-fg-muted") : ""}`}
            >
              {value}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

export default PlanCard;
